import React, { useEffect, useRef, useState, useCallback } from 'react';
import { API_BASE } from '../utils/api';

// --- Web Mercator Static Map Tile Caching & Loading ---
const tileImageCache = new Map<string, HTMLImageElement>();
const tileRetryCount = new Map<string, number>();

const getCachedTileImage = (z: number, x: number, y: number, onLoad: () => void): HTMLImageElement | null => {
  const key = `${z}/${x}/${y}`;
  if (tileImageCache.has(key)) {
    const img = tileImageCache.get(key)!;
    return img.complete ? img : null;
  }
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.src = `${API_BASE}/api/tiles/openstreetmap/${z}/${x}/${y}`;
  img.onload = () => {
    tileImageCache.set(key, img);
    tileRetryCount.delete(key);
    onLoad();
  };
  img.onerror = () => {
    tileImageCache.delete(key);
    const retries = tileRetryCount.get(key) || 0;
    console.error(`Failed to load tile: ${key} (retry ${retries}/3)`);
    if (retries < 3) {
      tileRetryCount.set(key, retries + 1);
      setTimeout(onLoad, 2000 + retries * 2000);
    }
  };
  tileImageCache.set(key, img);
  return null;
};

const lon2tile = (lon: number, zoom: number) => {
  return ((lon + 180) / 360) * Math.pow(2, zoom);
};

const lat2tile = (lat: number, zoom: number) => {
  return (
    ((1 - Math.log(Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)) / Math.PI) / 2) *
    Math.pow(2, zoom)
  );
};

interface CourseMiniatureCanvasProps {
  coordinates?: [number, number, number][];
  activityType: string;
  isHovered: boolean;
}

export const CourseMiniatureCanvas: React.FC<CourseMiniatureCanvasProps> = ({
  coordinates,
  activityType,
  isHovered,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [tilesLoaded, setTilesLoaded] = useState(0);
  const triggerRepaint = () => setTilesLoaded((c) => c + 1);

  const animationRef = useRef<{ pct: number; startTime: number | null }>({ pct: 1, startTime: null });
  const animationFrameId = useRef<number | null>(null);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 1. Get layout size and handle device pixel ratio for sharp retina rendering
    const w = canvas.clientWidth || 360;
    const h = canvas.clientHeight || 130;
    const dpr = window.devicePixelRatio || 1;

    // Scale backing buffer dynamically
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.scale(dpr, dpr);

    ctx.clearRect(0, 0, w, h);

    if (!coordinates || coordinates.length < 2) {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
      ctx.font = '12px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('No route path', w / 2, h / 2);
      return;
    }

    // 2. Calculate bounds
    let minLat = Infinity,
      maxLat = -Infinity,
      minLon = Infinity,
      maxLon = -Infinity;
    for (const [lon, lat] of coordinates) {
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lon < minLon) minLon = lon;
      if (lon > maxLon) maxLon = lon;
    }

    // 3. Find best zoom level to fit bounds
    let zoom = 16;
    for (let z = 16; z >= 1; z--) {
      const tileMinX = lon2tile(minLon, z);
      const tileMaxX = lon2tile(maxLon, z);
      const tileMinY = lat2tile(maxLat, z);
      const tileMaxY = lat2tile(minLat, z);
      const tileW = (tileMaxX - tileMinX) * 256;
      const tileH = (tileMaxY - tileMinY) * 256;
      const numTiles =
        (Math.floor(tileMaxX) - Math.floor(tileMinX) + 1) * (Math.floor(tileMaxY) - Math.floor(tileMinY) + 1);

      if (tileW <= w - 32 && tileH <= h - 32 && numTiles <= 6) {
        zoom = z;
        break;
      }
      zoom = z;
    }

    // 4. Coordinate mapping helpers
    const tileMinX = lon2tile(minLon, zoom);
    const tileMaxX = lon2tile(maxLon, zoom);
    const tileMinY = lat2tile(maxLat, zoom);
    const tileMaxY = lat2tile(minLat, zoom);
    const centerTileX = (tileMinX + tileMaxX) / 2;
    const centerTileY = (tileMinY + tileMaxY) / 2;

    const getCanvasCoords = (lon: number, lat: number) => {
      const x = w / 2 + (lon2tile(lon, zoom) - centerTileX) * 256;
      const y = h / 2 + (lat2tile(lat, zoom) - centerTileY) * 256;
      return { x, y };
    };

    // 5. Draw background tiles - Calculate visible tile range covering entire canvas
    const startTileX = Math.floor(centerTileX - w / 2 / 256);
    const endTileX = Math.floor(centerTileX + w / 2 / 256);
    const startTileY = Math.floor(centerTileY - h / 2 / 256);
    const endTileY = Math.floor(centerTileY + h / 2 / 256);

    for (let tx = startTileX; tx <= endTileX; tx++) {
      for (let ty = startTileY; ty <= endTileY; ty++) {
        const img = getCachedTileImage(zoom, tx, ty, triggerRepaint);
        if (img) {
          const tileCanvasX = w / 2 + (tx - centerTileX) * 256;
          const tileCanvasY = h / 2 + (ty - centerTileY) * 256;
          ctx.drawImage(img, tileCanvasX, tileCanvasY, 256, 256);
        }
      }
    }

    // Add dark glass overlay to make routes pop
    ctx.fillStyle = 'rgba(11, 15, 25, 0.45)';
    ctx.fillRect(0, 0, w, h);

    // 6. Draw route path up to anim pct
    const pct = animationRef.current.pct;
    const drawCount = Math.max(2, Math.floor(coordinates.length * pct));

    ctx.beginPath();
    for (let i = 0; i < drawCount; i++) {
      const [lon, lat] = coordinates[i];
      const { x, y } = getCanvasCoords(lon, lat);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }

    let strokeColor = '#3b82f6';
    let glowColor = 'rgba(59, 130, 246, 0.35)';
    if (activityType === 'running') {
      strokeColor = '#ff385c';
      glowColor = 'rgba(255, 56, 92, 0.35)';
    } else if (activityType === 'cycling') {
      strokeColor = '#10b981';
      glowColor = 'rgba(16, 185, 129, 0.35)';
    } else if (activityType === 'hiking') {
      strokeColor = '#f59e0b';
      glowColor = 'rgba(245, 158, 11, 0.35)';
    } else if (activityType === 'climbing') {
      strokeColor = '#a855f7';
      glowColor = 'rgba(168, 85, 247, 0.35)';
    }

    ctx.strokeStyle = glowColor;
    ctx.lineWidth = 6;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.shadowColor = strokeColor;
    ctx.shadowBlur = 6;
    ctx.stroke();

    ctx.shadowBlur = 0;
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = 2.5;
    ctx.stroke();

    // 7. Start/End Dots
    if (coordinates.length > 0) {
      const startCoords = getCanvasCoords(coordinates[0][0], coordinates[0][1]);
      ctx.beginPath();
      ctx.arc(startCoords.x, startCoords.y, 4.5, 0, 2 * Math.PI);
      ctx.fillStyle = '#10b981';
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1;
      ctx.fill();
      ctx.stroke();

      if (pct >= 0.99) {
        const lastPt = coordinates[coordinates.length - 1];
        const endCoords = getCanvasCoords(lastPt[0], lastPt[1]);
        ctx.beginPath();
        ctx.arc(endCoords.x, endCoords.y, 4.5, 0, 2 * Math.PI);
        ctx.fillStyle = '#ef4444';
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 1;
        ctx.fill();
        ctx.stroke();
      }
    }
  }, [coordinates, activityType]);

  useEffect(() => {
    if (isHovered) {
      animationRef.current = { pct: 0, startTime: null };
      const animate = (time: number) => {
        if (!animationRef.current.startTime) animationRef.current.startTime = time;
        const elapsed = time - animationRef.current.startTime;
        const duration = 800; // 800ms trace animation
        const pct = Math.min(1, elapsed / duration);
        animationRef.current.pct = pct;
        draw();
        if (pct < 1) {
          animationFrameId.current = requestAnimationFrame(animate);
        }
      };
      animationFrameId.current = requestAnimationFrame(animate);
    } else {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
        animationFrameId.current = null;
      }
      animationRef.current.pct = 1;
      draw();
    }

    return () => {
      if (animationFrameId.current) cancelAnimationFrame(animationFrameId.current);
    };
  }, [isHovered, coordinates, draw]);

  useEffect(() => {
    draw();
  }, [coordinates, tilesLoaded, draw]);



  return (
    <canvas
      ref={canvasRef}
      style={{
        width: '100%',
        height: '130px',
        background: 'rgba(0, 0, 0, 0.25)',
        borderRadius: '12px',
        display: 'block',
        border: '1px solid rgba(255, 255, 255, 0.04)',
      }}
    />
  );
};
